import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Star, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Stars = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`h-3.5 w-3.5 ${
          value && n <= value ? 'text-rose-gold fill-rose-gold' : 'text-muted-foreground/40'
        }`}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

export default function CommunityPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    api
      .get('/reviews/feed', { params: { limit: 50 } })
      .then(({ data }) => setReviews(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered =
    filter === 'all'
      ? reviews
      : reviews.filter((r) => r.item_type === filter);

  return (
    <div data-testid="community-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <MessageCircle className="h-3.5 w-3.5" /> Community feed
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          What other women are loving.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Honest feedback from the Radiance community — real ratings, real notes, on real DIY hacks and curated products.
        </p>
      </div>

      <div className="mt-8">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-3 max-w-md">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-card">All</TabsTrigger>
            <TabsTrigger value="hack" className="rounded-lg data-[state=active]:bg-card">Hacks</TabsTrigger>
            <TabsTrigger value="product" className="rounded-lg data-[state=active]:bg-card">Products</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-8" data-testid="community-feed">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-2xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl bg-card border-border/70 p-10 text-center">
            <Sparkles className="h-9 w-9 mx-auto text-rose-gold" />
            <p className="mt-3 font-display text-2xl text-ink">No community reviews yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Be the first — visit any hack or product and click <em className="not-italic font-medium">I tried this</em>.
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/diy">Browse DIY Lab</Link>
              </Button>
              <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/products">Open Curator</Link>
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((r) => {
              const item = r.item || {};
              const href =
                r.item_type === 'hack' && item.slug ? `/diy/${item.slug}` : '/products';
              return (
                <Link
                  key={r.id}
                  to={href}
                  data-testid="community-review-card"
                  className="group block"
                >
                  <Card className="card-hover bg-card border-border/70 rounded-2xl overflow-hidden h-full">
                    <div className="flex">
                      {item.image_url && (
                        <div className="relative w-28 sm:w-36 shrink-0 bg-cream-paper">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 p-4 sm:p-5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="bg-rose-tint text-ink text-xs font-semibold">
                              {(r.user_name || 'A')[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-medium">{r.user_name}</span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-auto">
                            {r.item_type === 'hack' ? 'DIY Hack' : 'Product'}
                          </span>
                        </div>
                        <p className="font-display text-lg text-ink leading-tight line-clamp-1">
                          {item.name || 'Item'}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2">
                          {r.rating && <Stars value={r.rating} />}
                          {r.tried && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-rose-gold border border-rose-tint bg-accent/40 rounded-full px-2 py-0.5">
                              Tried it
                            </span>
                          )}
                        </div>
                        {r.text && (
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                            {r.text}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
